import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardActionArea,
  Typography,
  IconButton,
  Divider,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

interface Pays {
  id: number;
  libelle: string;
  url_flag: string;
  url_image: string;
  url_infos: string;
  visible: string;
}

interface PaysGridProps {
  paysVisibles: Pays[];
  paysAutres?: Pays[];
  onSelectPays: (paysId: number) => void;
}

const storeUrlInLocalStorage = (url: string) => {
  try {
    localStorage.setItem('pays_url_infos', url);
  } catch (e) {
    console.error("Erreur lors de la sauvegarde de l'URL:", e);
  }
};

const PaysCard: React.FC<{ pays: Pays; onSelectPays: (id: number) => void; dimmed?: boolean }> = ({
  pays,
  onSelectPays,
  dimmed,
}) => (
  <Card
    sx={{
      borderRadius: 3,
      overflow: 'hidden',
      position: 'relative',
      boxShadow: dimmed ? '0 1px 8px rgba(0,0,0,0.07)' : '0 2px 12px rgba(0,0,0,0.1)',
      opacity: dimmed ? 0.75 : 1,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 30px rgba(0,0,0,0.18)',
        opacity: 1,
      },
    }}
  >
    <CardActionArea
      onClick={() => onSelectPays(pays.id)}
      sx={{ position: 'relative' }}
    >
      <Box
        sx={{
          width: '100%',
          height: dimmed ? { xs: 110, sm: 130, md: 140 } : { xs: 140, sm: 160, md: 180 },
          backgroundImage: `url(${pays.url_image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '60%',
            background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            width: 32,
            height: 22,
            borderRadius: '4px',
            overflow: 'hidden',
            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
          }}
        >
          <img
            src={pays.url_flag}
            alt={`${pays.libelle} flag`}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </Box>

        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            p: 1.5,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              color: 'white',
              fontWeight: 700,
              fontSize: dimmed
                ? { xs: '0.78rem', sm: '0.85rem', md: '0.9rem' }
                : { xs: '0.85rem', sm: '0.95rem', md: '1rem' },
              textShadow: '0 1px 4px rgba(0,0,0,0.5)',
              lineHeight: 1.2,
            }}
          >
            {pays.libelle}
          </Typography>
        </Box>
      </Box>
    </CardActionArea>

    <IconButton
      onClick={(e) => {
        e.stopPropagation();
        const fullUrl = pays.url_infos + '?appli=yes';
        storeUrlInLocalStorage(fullUrl);
        window.open(fullUrl, '_blank');
      }}
      sx={{
        position: 'absolute',
        top: 6,
        right: 6,
        width: 28,
        height: 28,
        backgroundColor: 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(4px)',
        '&:hover': { backgroundColor: 'rgba(255,255,255,0.95)' },
      }}
    >
      <HelpOutlineIcon sx={{ fontSize: 18, color: '#e53935' }} />
    </IconButton>
  </Card>
);

const PaysGrid: React.FC<PaysGridProps> = ({ paysVisibles, paysAutres, onSelectPays }) => {
  return (
    <Box
      sx={{
        width: '100%',
        overflowY: 'auto',
        p: { xs: 1, sm: 2 },
      }}
    >
      {/* Pays principaux (visibles) */}
      <Grid container spacing={2}>
        {paysVisibles.map((pays) => (
          <Grid item xs={6} sm={4} md={3} lg={3} key={pays.id}>
            <PaysCard pays={pays} onSelectPays={onSelectPays} />
          </Grid>
        ))}
      </Grid>

      {/* Autres pays */}
      {paysAutres && paysAutres.length > 0 && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 3 }}>
            <Divider sx={{ flex: 1, borderColor: 'rgba(255,255,255,0.3)' }} />
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.8)',
                fontWeight: 600,
                textShadow: '0 1px 4px rgba(0,0,0,0.3)',
                whiteSpace: 'nowrap',
              }}
            >
              Autres destinations
            </Typography>
            <Divider sx={{ flex: 1, borderColor: 'rgba(255,255,255,0.3)' }} />
          </Box>

          <Grid container spacing={2}>
            {paysAutres.map((pays) => (
              <Grid item xs={6} sm={4} md={3} lg={3} key={pays.id}>
                <PaysCard pays={pays} onSelectPays={onSelectPays} dimmed />
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default PaysGrid;
